import { useCallback, useMemo } from 'react';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type { FlightPlannerBlock } from '@voll-intelligence/genui-contract';
import type {
  PlannerSubmit,
  SearchSelectOption,
} from '@voll-intelligence/intelligence-ui-kit/flight-planner';
import { fmtDayMonth } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n, type GenUiLocale, type GenUiTranslate } from '../../../shared/ui/i18n.js';
import { useFlightPlannerPort } from '../../shared/flight_planner_port.js';
import { airportNaming } from '../../shared/ui/airport_naming.js';
import { useFlightAirportHistoryPort } from '../../shared/airport_history_port.js';
import {
  airportLabelOf,
  airportOptionOf,
} from '@voll-intelligence/intelligence-ui-kit/flight-planner';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { PlannerForm, useRecentAirports } from '@voll-intelligence/intelligence-ui-kit';
import { flightPlannerCopy } from '../../shared/ui/flight_planner_copy.js';
import { BLOCK_ELEVATION } from '../../../shared/ui/block_elevation.js';
import { flightSearchWait } from './flight_search_wait.js';

/**
 * Transcript cosmético do turno de busca — o map-guard do flight lê o `data`; o
 * texto é só a mensagem visível.
 *
 * **Ele passa pelo dicionário (#2327).** Era um template literal em pt-BR, e numa
 * sessão em espanhol o card inteiro saía traduzido enquanto o eco — a fala que a
 * PESSOA aparenta ter dito — voltava em português. Uma variante FECHADA por forma
 * de viagem, nunca concatenação: colar "e volta X" numa frase traduzida não
 * sobrevive ao primeiro idioma que muda a ordem ou a pontuação.
 *
 * Puro e com `t` por parâmetro (e não o hook aqui dentro): assim ele continua
 * testável sem montar o card.
 */
function searchTranscript(t: GenUiTranslate, sel: PlannerSubmit, locale: GenUiLocale): string {
  // A data do eco sai formatada como o card a MOSTRA (`15 set`), não em ISO cru:
  // o texto é a fala da pessoa, e ninguém diz "2026-09-15". Data irreal cai no ISO
  // — pior que a formatada, melhor que um buraco na frase.
  const day = (iso: string) => fmtDayMonth(iso, locale) || iso;
  if (sel.action === 'search_multi') {
    const legs = sel.legs
      .map((l) =>
        t('flightPlanner.searchTextMultiLeg', {
          origin: l.origin,
          destination: l.destination,
          date: day(l.date),
        }),
      )
      .join(', ');
    return t('flightPlanner.searchTextMulti', { legs });
  }
  const base = {
    origin: sel.origin,
    destination: sel.destination,
    outbound: day(sel.outboundDate),
  };
  return sel.returnDate
    ? t('flightPlanner.searchTextRoundTrip', { ...base, return: day(sel.returnDate) })
    : t('flightPlanner.searchTextOneWay', base);
}

/**
 * Host CHAT do PlannerForm: o card é só o adaptador de transporte. O miolo
 * (abas, rota, calendário+heat, multi-trechos, CTA) vive dispatch-free em
 * `planner_form.tsx`; aqui moram as quatro amarras do canal —
 *   heat READ  → `planner-preview` (o shell troca o bloco / mergeia `routeHeat`),
 *   submit     → `interaction` com o `challengeId` do bloco,
 *   shimmer    → `busy` do shell,
 *   aeroportos → port `flightPlanner` (Airport → opção, via `airportOptionOf`).
 *
 * A `note` NÃO é uma delas (#2207): sem rodapé, o card não tem onde pô-la — a
 * fala do turno é prosa acima do card no chat, e no kanban ela deixa de
 * acompanhar este kind por decisão de produto.
 */
export function FlightPlannerCard({
  ui,
  readOnly = false,
  superseded = false,
  busy = false,
  replay = false,
  dispatch,
}: Readonly<{
  ui: FlightPlannerBlock;
  readOnly?: boolean;
  /** Bloco remontado do histórico (ver `GenUiRenderProps.replay`): o guia
   *  fica, mas o primeiro campo não abre sozinho — o gesto já foi gasto. */
  replay?: boolean;
  /** A conversa andou depois deste card (ver `GenUiRenderProps.superseded`):
   *  o card segue acionável, mas o preenchimento guiado desliga — abrir o
   *  campo seguinte no meio da thread, com a conversa já abaixo, é gesto que
   *  ninguém pediu. */
  superseded?: boolean;
  // Shimmer do calendário, dirigido pelo shell: turno do agente em voo (recomputo
  // do bloco) OU um planner-preview (READ) em voo pra ESTE card.
  busy?: boolean;
  dispatch: GenUiDispatch;
}>) {
  // Uma leitura só do i18n para os dois usos: o `locale` que rotula o aeroporto no
  // combobox e o `t`/`locale` do eco da busca (#2327).
  const { t, locale } = useGenUiI18n();
  // Os textos do form no locale da conversa: memo por locale, uma identidade só.
  const plannerCopy = useMemo(() => flightPlannerCopy(locale), [locale]);
  const naming = useMemo(() => airportNaming(locale), [locale]);
  const plannerPort = useFlightPlannerPort();
  // Busca remota via port; ausente (preview/storybook sem provider) ⇒ lista vazia.
  // useCallback estabiliza a referência: o PlaceField tem onSearch nas deps do
  // effect de busca — uma arrow inline re-dispararia a busca a cada re-render do card.
  const searchAirports = useCallback(
    (term: string): Promise<SearchSelectOption[]> =>
      plannerPort
        ? plannerPort.searchAirports(term).then((as) => as.map((a) => airportOptionOf(a, naming)))
        : Promise.resolve([]),
    [plannerPort, naming],
  );
  const recentAirports = useRecentAirports(useFlightAirportHistoryPort(), naming);
  const travelers = useGenUiConfig().ports?.travelers;
  // A casca do form (`.cd.fpl`, no kit) veste a elevação do bloco da thread (#6296);
  // nos overlays do explorer e da bancada ela segue a de fora. `contents` não entra no layout.
  return (
    <div className={`contents ${BLOCK_ELEVATION}`}>
      <PlannerForm
        // O bloco satisfaz os dois shapes: `initial` = retrato do mount (mesma
        // semântica dos seeds useState(ui.*) que o card sempre teve); `heat` = a
        // vista viva que o shell reconcilia (substitui o bloco / mergeia routeHeat).
        initial={ui}
        heat={ui}
        heatLoading={busy}
        readOnly={readOnly}
        // #2207: o card do chat não tem saia — o disparo é a lupa only-icon dentro
        // da fileira. Os demais hosts do form seguem com o rodapé deles.
        inlineSearch
        // O preenchimento guiado do card (De → Para → Quando, e o foco na lupa):
        // só enquanto ele é o último da conversa — o agente pré-preenche o que
        // entendeu e o guia pede só o que faltou. Congelado, o form já o ignora.
        guided={!superseded}
        guidedOpen={replay ? 'never' : 'always'}
        searchAirports={searchAirports}
        recentAirports={recentAirports}
        cityOfAirport={naming.cityByAirport}
        airportLabel={(iata) => airportLabelOf(iata, naming)}
        travelerDirectory={travelers}
        copy={plannerCopy}
        locale={locale}
        // Reads AUTOMÁTICOS só com host vivo (mesma guarda `plannerPort` de sempre);
        // os explícitos (pick/swap/pager) despacham sempre, como antes.
        autoFetchHeat={Boolean(plannerPort)}
        fetchHeat={(req, meta) => {
          dispatch({
            type: 'planner-preview',
            blockId: ui.id,
            ...req,
            analytics: { trigger: meta?.trigger ?? 'user' },
          });
        }}
        onSubmit={(request) => {
          dispatch({
            type: 'interaction',
            interactionId: ui.challengeId,
            text: searchTranscript(t, request, locale),
            data: request,
            wait: flightSearchWait(request, {
              cityOf: naming.cityByAirport,
              requesterId: ui.requesterId,
              locale,
            }),
          });
        }}
      />
    </div>
  );
}
